// ---------------------------------------------------------------------------
// phone.js — THE phone formatting/normalizing utility (2026-07-02).
// Every screen that shows or accepts a phone number goes through here —
// no ad-hoc formatting per component.
//
//   normalizePhone('(828) 974-6493')  → '8289746493'   (canonical storage)
//   normalizePhone('+1 828-974-6493') → '8289746493'   (leading 1 stripped)
//   normalizePhone('974-6493 x12')    → '974-6493 x12' (non-standard: as-is)
//   formatPhone('8289746493')         → '(828) 974-6493'
//   formatPhone('974-6493 x12')       → '974-6493 x12'  (graceful degrade)
//   maskPhoneInput('82897464')        → '(828) 974-64'  (as-you-type)
// ---------------------------------------------------------------------------

// Canonical value for storage/matching: 10 digits for standard US numbers
// (11-digit with leading 1 collapses). Anything else returns trimmed
// original so extensions/international entries aren't mangled.
export function normalizePhone(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    const digits = s.replace(/\D/g, '');
    if (digits.length === 10) return digits;
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
    return s;
}

// Just the digits (for matching), regardless of standardness.
export function phoneDigits(raw) {
    return String(raw || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
}

// Display format: (828) 974-6493 for standard numbers; pass-through for
// anything we can't confidently format.
export function formatPhone(raw) {
    const n = normalizePhone(raw);
    if (/^\d{10}$/.test(n)) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
    return String(raw || '').trim();
}

// As-you-type mask. Feed it the raw input value on every change; it
// returns what the field should show. Deleting works naturally because
// we rebuild from digits each keystroke.
//   • Entries containing letters (e.g. "828-974-6493 x12") pass through
//     untouched so we never fight extension typing.
//   • Pure phone entry is hard-capped at 10 digits — overflow is dropped
//     rather than spilling into the formatted number (BUG-5). A leading
//     country-code 1 is stripped for any 11+ digit run.
export function maskPhoneInput(raw) {
    const s = String(raw || '');
    if (/[a-z]/i.test(s)) return s;
    const digits = s.replace(/\D/g, '');
    const d = (digits.startsWith('1') && digits.length >= 11) ? digits.slice(1) : digits;
    const c = d.slice(0, 10);
    if (c.length === 0) return '';
    if (c.length <= 3) return `(${c}`;
    if (c.length <= 6) return `(${c.slice(0, 3)}) ${c.slice(3)}`;
    return `(${c.slice(0, 3)}) ${c.slice(3, 6)}-${c.slice(6)}`;
}

// ── Multi-phone helpers (clients.phones jsonb, sql/068) ─────────────────
// Shape: [{ number, label, is_primary }] — number stored NORMALIZED.
// clients.phone stays synced to the primary so every legacy consumer
// (SMS sends, dispatch views, search, crew portal) keeps working.

export const PHONE_LABELS = ['Mobile', 'Landline', 'Work', 'Other'];

// Coerce whatever's on the row into a clean phones array. Falls back to
// wrapping the legacy single phone as primary Mobile.
export function phonesOf(client) {
    const arr = Array.isArray(client?.phones) ? client.phones.filter(p => p && p.number) : [];
    if (arr.length) {
        // Exactly one primary, always.
        if (!arr.some(p => p.is_primary)) arr[0] = { ...arr[0], is_primary: true };
        return arr.map(p => ({ number: p.number, label: p.label || 'Mobile', is_primary: !!p.is_primary }));
    }
    if (client?.phone) return [{ number: normalizePhone(client.phone), label: 'Mobile', is_primary: true }];
    return [];
}

export function primaryPhone(client) {
    const arr = phonesOf(client);
    return arr.find(p => p.is_primary)?.number || arr[0]?.number || '';
}
